/**
 * Single source of truth for how building-attribute data sources rank against
 * each other (WHA-3688). The dashboard header must resolve ONE authoritative
 * value per attribute, and historically the header and the building-data
 * sidebar encoded *opposite* IFC-vs-EHR orders (WHA-3561). Centralising the
 * order here means the header reads it from one place and flipping it later is
 * a one-line change.
 *
 * Authority, highest first: a user's explicit edit always wins; among the
 * machine sources the measured BIM model (IFC) outranks the EHR registry, which
 * outranks document extraction.
 */
export type AttributeSourceKind = "user" | "ifc" | "ehr" | "extractor";

/** Descending authority. Index 0 wins. */
export const SOURCE_PRECEDENCE: readonly AttributeSourceKind[] = [
  "user",
  "ifc",
  "ehr",
  "extractor",
] as const;

/**
 * Pick the value of the highest-precedence source present in `byKind`, per
 * {@link SOURCE_PRECEDENCE}. Returns undefined when no source is present.
 */
export function pickByPrecedence<T>(
  byKind: Partial<Record<AttributeSourceKind, T>>
): T | undefined {
  for (const kind of SOURCE_PRECEDENCE) {
    const candidate = byKind[kind];
    if (candidate !== undefined) return candidate;
  }
  return undefined;
}
